import { $selectedAwsRegion, ensureAwsFetchInterceptor, store } from "@moment/state";

export type AwsRuntime = {
	/**
	 * Dummy credentials to pass to AWS SDK v3 clients.
	 * Actual signing happens transparently via the fetch interceptor + Rust proxy.
	 *
	 * Usage:
	 * ```javascript
	 * const { STSClient, GetCallerIdentityCommand } = await import("https://cdn.skypack.dev/@aws-sdk/client-sts");
	 * const client = new STSClient({ region: env._aws.region, credentials: env._aws.credentials });
	 * const identity = await client.send(new GetCallerIdentityCommand({}));
	 * ```
	 */
	credentials: { accessKeyId: string; secretAccessKey: string };

	/** Region from the selected AWS login session, falls back to "us-east-1". */
	region: string;
};

/** Module-level cached region updated via store subscription. */
let cachedRegion: string | null = null;
let initialized = false;

/**
 * Create the `env._aws` object for code cells.
 *
 * On first call, installs a global `fetch` interceptor that routes requests to
 * `*.amazonaws.com` through the Rust proxy for SigV4 signing. This makes AWS SDK v3
 * clients work transparently — just pass `env._aws.credentials` and the SDK's network
 * calls are automatically signed with the selected profile's real credentials.
 */
export const createAwsRuntime = (): AwsRuntime => {
	if (!initialized) {
		initialized = true;

		// Install the shared AWS fetch interceptor from packages/state
		ensureAwsFetchInterceptor();

		// Cache region for synchronous access via the getter
		void Promise.resolve(store.get($selectedAwsRegion)).then((v) => {
			cachedRegion = v ?? null;
		});

		store.sub($selectedAwsRegion, () => {
			void Promise.resolve(store.get($selectedAwsRegion)).then((v) => {
				cachedRegion = v ?? null;
			});
		});
	}

	return {
		credentials: {
			accessKeyId: "_moment_proxy",
			secretAccessKey: "_moment_proxy",
		},

		get region() {
			return cachedRegion ?? "us-east-1";
		},
	};
};
